import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faPlugCircleBolt, faPlugCircleXmark } from '@fortawesome/free-solid-svg-icons';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import DeviceChecklistService from 'api/deviceChecklist/DeviceChecklistService';
import UsersService from 'api/users/UsersService';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import FotaLoginModal from 'screens/deviceChecklist/FotaLoginModal';

type Status = 'loading' | 'connected' | 'disconnected';

const Spinner: React.FC = () => (
    <svg className="animate-spin h-5 w-5 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
);

const FotaScreen: React.FC = () => {
    const { t } = useTranslation();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const isPlatformAdmin = UsersService.isPlatformAdmin(userProfile?.roles);

    const [status, setStatus] = useState<Status>('loading');
    const [email, setEmail] = useState<string | null>(null);
    const [loginOpen, setLoginOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);

    const checkStatus = useCallback(async () => {
        setStatus('loading');
        try {
            const result = await DeviceChecklistService.fotaStatus();
            setStatus(result.authenticated ? 'connected' : 'disconnected');
            setEmail(result.email ?? null);
        } catch {
            setStatus('disconnected');
            setEmail(null);
        }
    }, []);

    useEffect(() => {
        if (isPlatformAdmin) {
            checkStatus();
        }
    }, [isPlatformAdmin, checkStatus]);

    const handleLoginSuccess = useCallback(() => {
        setLoginOpen(false);
        checkStatus();
    }, [checkStatus]);

    const handleLogout = useCallback(async () => {
        setLoggingOut(true);
        try {
            await DeviceChecklistService.fotaLogout();
        } finally {
            setLoggingOut(false);
            checkStatus();
        }
    }, [checkStatus]);

    if (!isPlatformAdmin) {
        return <Navigate replace to="/403" />;
    }

    return (
        <ScreenTitle title={t('fota.title')}>
            <div className="max-w-md mx-auto px-2 sm:px-4 py-6 sm:py-10">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    {/* Header */}
                    <div className="bg-slate-900 px-5 py-4 flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center text-white">
                            <FontAwesomeIcon icon={status === 'connected' ? faPlugCircleBolt : faPlugCircleXmark} size="lg" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-white font-bold text-sm leading-tight m-0">Teltonika FOTA Web</p>
                            <p className="text-[11px] text-slate-400 mt-0.5 m-0">{t('fota.subtitle')}</p>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="p-5">
                        {status === 'loading' && (
                            <div className="flex items-center gap-3 text-slate-500 text-sm py-4 justify-center">
                                <Spinner />
                                <span>{t('fota.status_checking')}</span>
                            </div>
                        )}

                        {status === 'connected' && (
                            <div className="space-y-4">
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 space-y-1">
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon icon={faCircleCheck} className="text-emerald-500 text-lg" />
                                        <span className="text-emerald-700 text-sm font-medium">{t('fota.status_connected')}</span>
                                    </div>
                                    {email && (
                                        <p className="text-emerald-600 text-xs m-0 pl-8">{email}</p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    disabled={loggingOut}
                                    className="w-full flex items-center justify-center gap-2 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 active:scale-95 transition-all disabled:opacity-50 bg-white"
                                >
                                    {loggingOut ? <Spinner /> : t('fota.logout_button')}
                                </button>
                            </div>
                        )}

                        {status === 'disconnected' && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                                    <FontAwesomeIcon icon={faCircleXmark} className="text-amber-500 text-lg" />
                                    <span className="text-amber-700 text-sm font-medium">{t('fota.status_not_connected')}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setLoginOpen(true)}
                                    className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 active:scale-95 text-white text-sm font-semibold rounded-xl px-4 py-2.5 border-0 transition-all"
                                >
                                    {t('fota.login_button')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <FotaLoginModal
                open={loginOpen}
                onClose={() => setLoginOpen(false)}
                onSuccess={handleLoginSuccess}
            />
        </ScreenTitle>
    );
};

export default FotaScreen;
